import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import './style.css';
import { isUnitLevelUser, isSquadLevelUser } from '../../helper-functions';

interface PrepopulatedData {
  assignee: string;
  from: number;
  to: number;
  certificatePrefix?: string;
}

interface User {
  user_id: string;
  username: string;
  location_name: string;
  location_code: string;
}

interface Booklet {
  booklet_id: number;
  booklet_prefix: string;
  book_start_no: number;
  book_end_no: number;
  noOfCertificatesIssued: number;
  totalNoOfCertificates: number;
}

interface AssignCertificateModalProps {
  onClose: () => void;
  onSubmit: (formData: any) => void;
  users: User[];
  booklets: Booklet[];
  prepopulatedData?: PrepopulatedData | null;
}

const AssignCertificateModal: React.FC<AssignCertificateModalProps> = ({
  onClose,
  onSubmit,
  users,
  booklets,
  prepopulatedData
}) => {
  const isEditMode = !!prepopulatedData;
  const loggedInUser = useSelector((state: any) => state.formConfig.user);
  // Unit-level user (e.g. License Manager) assigns to squads;
  // squad-level user (e.g. Approver) assigns to territories.
  const assigneeLabel = isUnitLevelUser(loggedInUser)
    ? 'Squad'
    : isSquadLevelUser(loggedInUser)
      ? 'Territory'
      : 'Territory';

  const getInitialSelectedUser = (data: any, allUsers: User[]) => {
    if (!data || !data.assignee) return null;
    if (data.location_code) {
      return allUsers.find(u => u.user_id === data.assignee && u.location_code === data.location_code) || null;
    }
    return allUsers.find(u => u.user_id === data.assignee) || null;
  };

  // Get unique prefixes from booklets
  const uniquePrefixes = Array.from(new Set(booklets.map(b => b.booklet_prefix)));

  const [selectedUser, setSelectedUser] = useState<User | null>(getInitialSelectedUser(prepopulatedData, users));
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPrefixDropdownOpen, setIsPrefixDropdownOpen] = useState(false);
  const [from, setFrom] = useState(String(prepopulatedData?.from || ''));
  const [to, setTo] = useState(String(prepopulatedData?.to || ''));
  const [certificatePrefix, setCertificatePrefix] = useState(prepopulatedData?.certificatePrefix || '');
  const [prefixError, setPrefixError] = useState('');
  const [fromError, setFromError] = useState('');
  const [toError, setToError] = useState('');
  const [availableRanges, setAvailableRanges] = useState<string[]>([]);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const prefixDropdownRef = React.useRef<HTMLDivElement>(null);

  // Update available ranges when prefix changes
  useEffect(() => {
    if (certificatePrefix) {
      const ranges: string[] = [];
      const bookletForPrefix = booklets.filter(b => b.booklet_prefix === certificatePrefix);
      
      if (bookletForPrefix.length > 0) {
        bookletForPrefix.forEach(booklet => {
          const issued = booklet.noOfCertificatesIssued;
          const total = booklet.totalNoOfCertificates;
          
          if (issued < total) {
            const availableStart = booklet.book_start_no + issued;
            const availableEnd = booklet.book_end_no;
            ranges.push(`${certificatePrefix}${availableStart} - ${certificatePrefix}${availableEnd}`);
          }
        });
      }
      
      setAvailableRanges(ranges);
      if (!isEditMode) {
        setFrom('');
        setTo('');
      }
    } else {
      setAvailableRanges([]);
    }
  }, [certificatePrefix, booklets, isEditMode]);

  useEffect(() => {
    setSelectedUser(getInitialSelectedUser(prepopulatedData, users));
    setFrom(String(prepopulatedData?.from || ''));
    setTo(String(prepopulatedData?.to || ''));
    setCertificatePrefix(prepopulatedData?.certificatePrefix || '');
    setPrefixError('');
  }, [prepopulatedData, users]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (prefixDropdownRef.current && !prefixDropdownRef.current.contains(event.target as Node)) {
        setIsPrefixDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update available ranges when prefix changes
  useEffect(() => {
    if (certificatePrefix) {
      const ranges: string[] = [];
      const bookletForPrefix = booklets.filter(b => b.booklet_prefix === certificatePrefix);
      
      if (bookletForPrefix.length > 0) {
        bookletForPrefix.forEach(booklet => {
          const issued = booklet.noOfCertificatesIssued;
          const total = booklet.totalNoOfCertificates;
          
          if (issued < total) {
            const availableStart = booklet.book_start_no + issued;
            const availableEnd = booklet.book_end_no;
            ranges.push(`${certificatePrefix}${availableStart} - ${certificatePrefix}${availableEnd}`);
          }
        });
      }
      
      setAvailableRanges(ranges);
      if (!isEditMode) {
        setFrom('');
        setTo('');
      }
    } else {
      setAvailableRanges([]);
    }
  }, [certificatePrefix, booklets, isEditMode]);

  const prefixRegex = /^[a-zA-Z0-9/\-()]+$/;

  const handlePrefixSelect = (prefix: string) => {
    setCertificatePrefix(prefix);
    setIsPrefixDropdownOpen(false);
    setPrefixError('');
  };

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFrom(value);

    if (!isEditMode) {
      setTo('');
      setToError('');
    }

    if (value && parseInt(value) > 0) {
      setFromError('');
    }
  };

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTo(value);

    if (from && parseInt(value) <= parseInt(from)) {
      setToError('To must be higher than From.');
    } else {
      setToError('');
    }
  };

  const handlePrefixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only letters, digits, slash, dash, parentheses
    const sanitized = value.replace(/[^a-zA-Z0-9/\-()]/g, '');
    setCertificatePrefix(sanitized);
    if (sanitized && !prefixRegex.test(sanitized)) {
      setPrefixError('Only letters, numbers, /, -, ( and ) are allowed');
    } else {
      setPrefixError('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isApproverUser = loggedInUser?.role === 'APPROVER';
    
    if (isApproverUser) {
      // APPROVER validation - prefix must be selected
      if (from === '' || to === '' || !selectedUser || !certificatePrefix) {
        setFromError(from === '' ? 'From is required.' : '');
        setToError(to === '' ? 'To is required.' : '');
        setPrefixError(!certificatePrefix ? 'Please select a certificate prefix.' : '');
        return;
      }
    } else {
      // Non-APPROVER validation - prefix is required
      if (from === '' || to === '' || !selectedUser || !certificatePrefix) {
        setFromError(from === '' ? 'From is required.' : '');
        setToError(to === '' ? 'To is required.' : '');
        setPrefixError(!certificatePrefix ? 'Certificate Prefix is required.' : '');
        return;
      }

      if (certificatePrefix && !prefixRegex.test(certificatePrefix)) {
        setPrefixError('Only letters, numbers, /, -, ( and ) are allowed');
        return;
      }
    }

    if (toError || fromError || prefixError) return;

    const formData = {
      assignee: selectedUser.user_id,
      from: parseInt(from),
      to: parseInt(to),
      certificatePrefix: certificatePrefix || '',
      mode: isEditMode ? 'EDIT' : 'ASSIGN',
      location_code: selectedUser.location_code,
      location_name: selectedUser.location_name,
    };

    onSubmit(formData);
    onClose();
  };
  const isApproverUser = loggedInUser?.role === 'APPROVER';

  return (
    <div className="modal-overlay">
      <div className={`modal-content ${isApproverUser ? 'modal-content--approver' : ''}`}>
        <div className="modal-headers" style={{position:'relative'}}>
          <span className="modal-heading">{isEditMode ? 'Edit Assignment' : 'Assign Booklet'}</span>
          <button className="modal-close-button" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          {isApproverUser ? (
            <>
             <div className="form-group">
                <label className="modallabel">Assign to <mark style={{ color: 'red', background: 'transparent' }}>*</mark></label>
                <div className="custom-dropdown" ref={dropdownRef}>
                  <div
                    className={`custom-dropdown-trigger ${isDropdownOpen ? 'open' : ''}`}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <div className="custom-dropdown-value">
                      {selectedUser ? (
                        <>
                          <span className="dropdown-location">{selectedUser.location_name}</span>
                          <span className="dropdown-username">{selectedUser.username}</span>
                        </>
                      ) : (
                        <span className="dropdown-placeholder">Select a {assigneeLabel}</span>
                      )}
                    </div>
                    <span className={`dropdown-arrow ${isDropdownOpen ? 'up' : ''}`}>&#9662;</span>
                  </div>
                  {isDropdownOpen && (
                    <div className="custom-dropdown-options">
                      {[...users].sort((a: User, b: User) => a.location_name.localeCompare(b.location_name)).map((user: User) => (
                        <div
                          key={`${user.user_id}-${user.location_code}`}
                          className={`custom-dropdown-option ${selectedUser?.user_id === user.user_id && selectedUser?.location_code === user.location_code ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedUser(user);
                            setIsDropdownOpen(false);
                          }}
                        >
                          <div className="option-content">
                            <span className="option-location">{user.location_name}</span>
                            <span className="option-username">{user.username}</span>
                          </div>
                          {selectedUser?.user_id === user.user_id && selectedUser?.location_code === user.location_code && (
                            <span className="option-check">&#10003;</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label className="modallabel">Certificate no. Range  <mark style={{ color: 'red', background: 'transparent' }}>*</mark></label>
                <div className="custom-dropdown" ref={prefixDropdownRef}>
                  <div
                    className={`custom-dropdown-trigger ${isPrefixDropdownOpen ? 'open' : ''} ${isEditMode ? 'disabled' : ''}`}
                    onClick={() => !isEditMode && setIsPrefixDropdownOpen(!isPrefixDropdownOpen)}
                    style={isEditMode ? { pointerEvents: 'none', opacity: 0.6 } : {}}
                  >
                    <div className="custom-dropdown-value">
                      {certificatePrefix ? (
                        <span className="dropdown-prefix">{certificatePrefix}</span>
                      ) : (
                        <span className="dropdown-placeholder">Select Prefix</span>
                      )}
                    </div>
                    <span className={`dropdown-arrow ${isPrefixDropdownOpen ? 'up' : ''}`}>&#9662;</span>
                  </div>
                  {isPrefixDropdownOpen && !isEditMode && (
                    <div className="custom-dropdown-options">
                      {uniquePrefixes.map((prefix: string) => (
                        <div
                          key={prefix}
                          className={`custom-dropdown-option ${certificatePrefix === prefix ? 'selected' : ''}`}
                          onClick={() => handlePrefixSelect(prefix)}
                        >
                          <div className="option-content">
                            <span className="option-prefix">{prefix}</span>
                          </div>
                          {certificatePrefix === prefix && (
                            <span className="option-check">&#10003;</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="range-inputs">
                  <input
                    type="number"
                    placeholder="From"
                    value={from}
                    onChange={handleFromChange}
                    disabled={!certificatePrefix}
                    required
                  />
                  <span>-</span>
                  <input
                    type="number"
                    placeholder="To"
                    value={to}
                    onChange={handleToChange}
                    disabled={!from || !certificatePrefix}
                    required
                  />
                </div>
                {toError && <p className="error-message">{toError}</p>}
                {availableRanges.length > 0 && (
                  <div className="available-ranges">
                    <p style={{ fontSize: 12, fontWeight: 500, marginBottom: 8, color: '#333' }}>Available Range:</p>
                    <ul style={{ fontSize: 12, color: '#666', margin: 0, paddingLeft: 20 }}>
                      {availableRanges.map((range, index) => (
                        <li key={index}>{range}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

             
            </>
          ) : (
            <>
              <div className="form-group">
                <label className="modallabel">Select {assigneeLabel} <mark style={{ color: 'red', background: 'transparent' }}>*</mark></label>
                <div className="custom-dropdown" ref={dropdownRef}>
                  <div
                    className={`custom-dropdown-trigger ${isDropdownOpen ? 'open' : ''}`}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <div className="custom-dropdown-value">
                      {selectedUser ? (
                        <>
                          <span className="dropdown-location">{selectedUser.location_name}</span>
                          <span className="dropdown-username">{selectedUser.username}</span>
                        </>
                      ) : (
                        <span className="dropdown-placeholder">Select a {assigneeLabel}</span>
                      )}
                    </div>
                    <span className={`dropdown-arrow ${isDropdownOpen ? 'up' : ''}`}>&#9662;</span>
                  </div>
                  {isDropdownOpen && (
                    <div className="custom-dropdown-options">
                      {[...users].sort((a: User, b: User) => a.location_name.localeCompare(b.location_name)).map((user: User) => (
                        <div
                          key={`${user.user_id}-${user.location_code}`}
                          className={`custom-dropdown-option ${selectedUser?.user_id === user.user_id && selectedUser?.location_code === user.location_code ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedUser(user);
                            setIsDropdownOpen(false);
                          }}
                        >
                          <div className="option-content">
                            <span className="option-location">{user.location_name}</span>
                            <span className="option-username">{user.username}</span>
                          </div>
                          {selectedUser?.user_id === user.user_id && selectedUser?.location_code === user.location_code && (
                            <span className="option-check">&#10003;</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="modallabel">Certificate Prefix <mark style={{ color: 'red', background: 'transparent' }}>*</mark></label>
                <input
                  type="text"
                  style={{height:50}}
                  placeholder="e.g. MH/2026"
                  value={certificatePrefix}
                  onChange={handlePrefixChange}
                  maxLength={50}
                  required
                />
                {prefixError && <p className="error-message">{prefixError}</p>}
                {certificatePrefix && !prefixError && from && (
                  <p style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                    Preview: <strong>{certificatePrefix} {from}</strong>
                    {to && <> to <strong>{certificatePrefix} {to}</strong></>}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label className="modallabel">Certificate no. Range  <mark style={{ color: 'red', background: 'transparent' }}>*</mark></label>
                <div className="range-inputs">
                  <input
                    type="number"
                    placeholder="From"
                    value={from}
                    onChange={handleFromChange}
                    required
                  />
                  <span>-</span>
                  <input
                    type="number"
                    placeholder="To"
                    value={to}
                    onChange={handleToChange}
                    disabled={!from}
                    required
                  />
                </div>
                {toError && <p className="error-message">{toError}</p>}
              </div>
            </>
          )}

          <div className="form-actions">
            <button type="submit" className="assign-button-modal">
              {isEditMode ? 'Save Changes' : 'Assign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignCertificateModal;